import React, { Fragment, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { changeRole, resetFilters } from "../redux/rolesSagas";
import { CCollapse, CFormText, CInput } from "@coreui/react";
import uniq from "lodash/uniq";
import isEqual from "lodash/isEqual";
import { MainModal } from "../../../../components/modal/MainModal";
import { ErrorList } from "../../../../components/ErrorList";
import {
  extendPermissionByCustom,
  rearrangeDailyEmailsPermissions
} from "../../../../utils/permissionsUtils";

export const Edit = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const [permissionTypeKey, setPermissionTypeKey] = useState("");

  const [currentGroupName, setCurrentGroupName] = useState("");

  const [currentPermissionsKey, setCurrentPermissionsKey] = useState("permissions");

  const [title, setTitle] = useState("");
  const {
    selected: {
      name,
      permissions = [],
      reporting_portal_permissions = [],
      organization: { name: orgName }
    },
    permissions: commonPermissions,
    status,
    errors
  } = useSelector((state) => state.roles);

  let { permissions: formattedPermissions, daily } =
    rearrangeDailyEmailsPermissions(commonPermissions);

  const allPermissions = extendPermissionByCustom(formattedPermissions, orgName);

  let [selectedPermissions, setSelectedPermissions] = useState({
    permissions: [],
    reporting_portal_permissions: []
  });

  const save = () => {
    dispatch(changeRole({ selectedPermissions, name: title }));
  };

  useEffect(() => {
    setSelectedPermissions({ permissions, reporting_portal_permissions });
    setTitle(name);
  }, [permissions, reporting_portal_permissions]);

  useEffect(() => {
    if (status == "success") {
      close();
    }
  }, [status]);

  const close = () => {
    dispatch(resetFilters());
    setCurrentGroupName("");
    setSelectedPermissions({
      permissions: [],
      reporting_portal_permissions: []
    });
    setPermissionTypeKey("");
    onClose();
  };

  const arePermissionsDirty =
    !isEqual(permissions.sort(), selectedPermissions.permissions.sort()) ||
    !isEqual(
      reporting_portal_permissions.sort(),
      selectedPermissions.reporting_portal_permissions.sort()
    );

  const primaryButtonDisabled = !(arePermissionsDirty || (title && title.trim() && title !== name));

  const onInputChange = (title) => {
    setTitle(title);
  };

  const onPermissionTypeClick = (type) => {
    setPermissionTypeKey(type);
    setCurrentPermissionsKey(type === "reporting_portal_permissions" ? type : "permissions");

    if (type === permissionTypeKey) {
      setPermissionTypeKey("");
      setCurrentGroupName("");
    }
  };

  const onGroupPermissionClick = (e) => {
    let targetText = e.target.innerText;
    let label = e.target.parentElement.dataset.label;
    let groupName = label ? label + "-" + targetText : targetText;

    if (groupName === currentGroupName) {
      setCurrentGroupName("");
      return;
    }

    setCurrentGroupName(groupName);
  };

  const updatePermissions = (checked, ids) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [currentPermissionsKey]: checked
        ? uniq([...prev[currentPermissionsKey], ...ids])
        : prev[currentPermissionsKey].filter((id) => !ids.includes(id))
    }));
  };

  const onPermissionClick = (e) => {
    e.stopPropagation();
    updatePermissions(e.target.checked, [+e.target.value]);
  };

  const onAllClick = ({ e, options }) => {
    updatePermissions(
      e.target.checked,
      options.map((p) => p.id)
    );
  };

  const isAllChecked = ({ options }) => {
    let allPermissionIdsPerGroup = options.map((permission) => permission.id);
    return allPermissionIdsPerGroup.every((id) =>
      selectedPermissions[currentPermissionsKey].includes(id)
    );
  };

  const handleStyles = (permission) => {
    let isSeparate = permission.name === "Separate emails for each location";

    return {
      display: !isSeparate
        ? "block"
        : selectedPermissions.permissions.includes(daily[0].id)
        ? "block"
        : "none",
      marginLeft: isSeparate ? "20px" : "0px"
    };
  };

  const renderInnerControls = ({ id, group, options }) => (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ paddingLeft: "40px", paddingBottom: "15px", width: "100%" }}
    >
      <div className="form-check" key={group}>
        <input
          className="form-check-input"
          type="checkbox"
          value={group}
          id={id ? id : group}
          onChange={(e) => onAllClick({ e, options })}
          checked={isAllChecked({ options })}
        />
        <label className="form-check-label" htmlFor={id ? id : group}>
          Check All / Uncheck All
        </label>
      </div>
      {options.map((permission) => {
        return (
          <div className="form-check" key={permission.id} style={handleStyles(permission)}>
            <input
              className="form-check-input"
              type="checkbox"
              value={permission.id}
              id={permission.id}
              onChange={onPermissionClick}
              checked={selectedPermissions[currentPermissionsKey].includes(permission.id)}
            />
            <label className="form-check-label" htmlFor={permission.id}>
              {permission.name}
            </label>
          </div>
        );
      })}
    </div>
  );
  const renderGroups = (groupParent, hideInnerCaption) => {
    return (
      <div onClick={(e) => onGroupPermissionClick(e)}>
        <CCollapse show={groupParent === permissionTypeKey}>
          {Object.keys(allPermissions[groupParent]).map((parent) => {
            let isParent = !Array.isArray(allPermissions[groupParent][parent]);

            if (isParent) {
              return (
                <div key={parent} data-label={parent}>
                  <div style={{ marginLeft: "20px" }}>{parent}</div>

                  {Object.keys(allPermissions[groupParent][parent]).map((group) => {
                    let id = parent + "-" + group;
                    return (
                      <Fragment key={id}>
                        <div className="group__item" style={{ marginLeft: "20px" }}>
                          {group}
                        </div>
                        <CCollapse show={id === currentGroupName}>
                          {renderInnerControls({
                            id,
                            group,
                            groupParent,
                            options: allPermissions[groupParent][parent][group]
                          })}
                        </CCollapse>
                      </Fragment>
                    );
                  })}
                </div>
              );
            }

            return hideInnerCaption ? (
              <Fragment key={parent}>
                {renderInnerControls({
                  group: parent,
                  groupParent,
                  options: allPermissions[groupParent][parent]
                })}
              </Fragment>
            ) : (
              <Fragment key={parent}>
                <div className="group__item" style={{ marginLeft: "20px" }}>
                  {parent}
                </div>
                <CCollapse show={parent === currentGroupName}>
                  {renderInnerControls({
                    group: parent,
                    groupParent,
                    options: allPermissions[groupParent][parent]
                  })}
                </CCollapse>
              </Fragment>
            );
          })}
        </CCollapse>
      </div>
    );
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        onClose={close}
        parentSelector=".roles"
        headerText="Edit Role"
        headerCloseHandler={close}
        bodyClassName="main-popup-body"
        containerClassName="edit"
        primaryButtonText="Save"
        primaryButtonDisabled={primaryButtonDisabled}
        primaryBtnHandler={() => save()}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        height="auto"
        width={900}
      >
        <>
          <div className="light group">
            <div className=" ">
              <p>Name</p>
              <CInput
                onChange={(e) => onInputChange(e.target.value, "name")}
                className="text-input"
                type="text"
                placeholder="Add Name"
                value={title}
              />
              <CFormText className="help-block">
                {name && name.trim() ? "" : "* Please add name"}
              </CFormText>
            </div>

            <div className="">
              <p>Edit Permissions</p>
              <CFormText className="help-block">
                {arePermissionsDirty ? "" : "* Please choose new permissions"}
              </CFormText>
              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("admin_permissions")}
              >
                Admin Permissions
              </div>
              {renderGroups("admin_permissions")}

              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("charts_permissions")}
              >
                Chart Permissions
              </div>

              {renderGroups("charts_permissions")}

              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("reporting_portal_permissions")}
              >
                Reporting Portal Permissions
              </div>
              {renderGroups("reporting_portal_permissions")}

              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("notifications_emails_goals")}
              >
                Notifications / Emails / Goals
              </div>

              {renderGroups("notifications_emails_goals", true)}
            </div>
          </div>

          <ErrorList errors={errors} />
        </>
      </MainModal>
    </>
  );
};
